export interface Limits {
  near_full: number;
  small_container_weeks: number;
  rate_band: number;
  high_scrap: number;
  default_scrap: number;
}

export interface LimitsForm {
  nearFull: string;
  smallWeeks: string;
  rateBand: string;
  highScrap: string;
  defaultScrap: string;
}

const percent = (share: number) => String(Math.round(share * 1000) / 10);

/** The page shows shares as percents, and the rate band as the percent above or below the stated rate. */
export function limitsForm(limits: Limits): LimitsForm {
  return {
    nearFull: percent(limits.near_full),
    smallWeeks: String(limits.small_container_weeks),
    rateBand: percent(limits.rate_band - 1),
    highScrap: percent(limits.high_scrap),
    defaultScrap: percent(limits.default_scrap),
  };
}

export interface LimitError {
  key: keyof LimitsForm;
  text: string;
}

/** The allowed range of each field, in the units of the form. */
export const RANGES: Record<keyof LimitsForm, { low: number; high: number; unit: "%" | "weeks"; name: string }> = {
  nearFull: { low: 50, high: 100, unit: "%", name: "Near full" },
  smallWeeks: { low: 1, high: 26, unit: "weeks", name: "Small container" },
  rateBand: { low: 5, high: 200, unit: "%", name: "The rate flag" },
  highScrap: { low: 5, high: 90, unit: "%", name: "High scrap" },
  defaultScrap: { low: 0, high: 90, unit: "%", name: "The default scrap" },
};

const amount = (value: string | number, unit: "%" | "weeks") => (unit === "%" ? `${value}%` : `${value} weeks`);
const span = (low: number, high: number, unit: "%" | "weeks") =>
  unit === "%" ? `${low}% to ${high}%` : `${low} to ${high} weeks`;

export function rangeText(key: keyof LimitsForm, defaults: LimitsForm): string {
  const { low, high, unit } = RANGES[key];
  return `From ${span(low, high, unit)}. Default ${amount(defaults[key], unit)}.`;
}

function number(text: string, low: number, high: number): number | null {
  const clean = text.trim().replace(/\s*%$/, "");
  const value = Number(clean);
  return clean !== "" && Number.isFinite(value) && value >= low && value <= high ? value : null;
}

/** What a rate band in the form does: the high flag is above band, the low flag is below 1 / band. */
export function rateEffect(bandText: string): string | null {
  const band = number(bandText, RANGES.rateBand.low, RANGES.rateBand.high);
  if (band === null) return null;
  const factor = 1 + band / 100;
  return `Rate high above ${Math.round(100 * factor)}% of the stated rate. Rate low below ${Math.round(100 / factor)}% of it.`;
}

export function limitsChange(form: LimitsForm): Limits | LimitError {
  const values = {} as Record<keyof LimitsForm, number>;
  for (const key of Object.keys(RANGES) as (keyof LimitsForm)[]) {
    const { low, high, unit, name } = RANGES[key];
    const value = number(form[key], low, high);
    if (value === null) return { key, text: `${name} must be from ${span(low, high, unit)}.` };
    values[key] = value;
  }
  const share = (value: number) => Math.round(value * 10) / 1000;
  return {
    near_full: share(values.nearFull),
    small_container_weeks: values.smallWeeks,
    rate_band: Math.round((1 + values.rateBand / 100) * 1000) / 1000,
    high_scrap: share(values.highScrap),
    default_scrap: share(values.defaultScrap),
  };
}
